import React, { useState, useEffect, useCallback } from 'react';
import api from '../../services/api';
import './AdminContact.css';

function fmt(dateStr) {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('th-TH', { dateStyle: 'medium', timeStyle: 'short' });
}
function maskCid(cid) {
    return cid ? cid.replace(/^(\d{1})\d{8}(\d{4})$/, '$1********$2') : '-';
}

// ป้ายประเภทคำร้อง — ต้องตรงกับ CONTACT_CATEGORIES ฝั่ง server
const CATEGORY_LABELS = {
    position: 'ขอแก้ชื่อตำแหน่ง',
    name: 'ขอแก้ชื่อ-สกุล / ชื่อผิด',
    salary: 'ขอแก้ข้อมูลเงินเดือน',
    document: 'สอบถามเรื่องเอกสาร',
    general: 'อื่นๆ / สอบถามทั่วไป'
};
function parseAttachments(raw) {
    if (!raw) return [];
    if (Array.isArray(raw)) return raw;
    try { return JSON.parse(raw) || []; } catch (e) { return []; }
}

function AdminContact() {
    const [filter, setFilter] = useState('new');
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [replyFor, setReplyFor] = useState(null);
    const [replyText, setReplyText] = useState('');
    const [sending, setSending] = useState(false);
    const [message, setMessage] = useState({ type: '', text: '' });

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const res = await api.get('/contact/admin/list', { params: { status: filter } });
            setItems(res.data || []);
        } catch (err) {
            setMessage({ type: 'error', text: err.response?.data?.error || 'โหลดข้อมูลไม่สำเร็จ' });
        } finally {
            setLoading(false);
        }
    }, [filter]);

    useEffect(() => { fetchData(); }, [fetchData]);

    const sendReply = async (id) => {
        if (!replyText.trim()) {
            setMessage({ type: 'error', text: 'กรุณาพิมพ์คำตอบ' });
            return;
        }
        setSending(true);
        setMessage({ type: '', text: '' });
        try {
            const res = await api.post(`/contact/admin/${id}/reply`, { reply: replyText.trim() });
            setMessage({ type: 'success', text: res.data.message || 'ส่งคำตอบแล้ว' });
            setReplyFor(null);
            setReplyText('');
            fetchData();
        } catch (err) {
            setMessage({ type: 'error', text: err.response?.data?.error || 'ส่งคำตอบไม่สำเร็จ' });
        } finally {
            setSending(false);
        }
    };

    return (
        <div className="admin-page">
            <div className="admin-header">
                <h1>ข้อความจากผู้ใช้</h1>
                <p>ข้อความที่ผู้ใช้ส่งเข้ามา — ตอบกลับจะส่งทางอีเมลถึงผู้ใช้</p>
            </div>

            {message.text && (
                <div className={`alert alert-${message.type}`} style={{ marginBottom: 16 }}>{message.text}</div>
            )}

            <div className="contact-filter-tabs">
                {[{ k: 'new', l: 'รอตอบ' }, { k: 'replied', l: 'ตอบแล้ว' }, { k: 'all', l: 'ทั้งหมด' }].map(t => (
                    <button key={t.k} className={`filter-tab ${filter === t.k ? 'active' : ''}`} onClick={() => setFilter(t.k)}>
                        {t.l}
                    </button>
                ))}
            </div>

            {loading ? (
                <div className="flex-center" style={{ padding: 40 }}><div className="spinner"></div></div>
            ) : items.length === 0 ? (
                <div className="steam-card"><div className="steam-card-body" style={{ textAlign: 'center', color: '#9ca3af', padding: 30 }}>ไม่มีข้อความ</div></div>
            ) : (
                <div className="contact-admin-list">
                    {items.map(m => {
                        const atts = parseAttachments(m.attachments);
                        return (
                        <div key={m.id} className="steam-card contact-admin-item">
                            <div className="steam-card-body">
                                <div className="cai-head">
                                    <div>
                                        <span className="cai-name">{m.fullname || '-'}</span>
                                        <span className="cai-meta">{maskCid(m.cid)} · {m.email}</span>
                                    </div>
                                    {m.status === 'replied'
                                        ? <span className="badge badge-success">ตอบแล้ว</span>
                                        : <span className="badge badge-warning">รอตอบ</span>}
                                </div>
                                <div className="cai-time">{fmt(m.created_at)}</div>
                                {m.category && (
                                    <span className={`cai-cat-tag${m.category !== 'general' ? ' cai-cat-tag-evidence' : ''}`}>
                                        {CATEGORY_LABELS[m.category] || m.category}
                                    </span>
                                )}
                                <div className="cai-message">{m.message}</div>
                                {atts.length > 0 && (
                                    <div className="cai-attachments">
                                        <span className="cai-att-label">หลักฐานแนบ ({atts.length}):</span>
                                        <div className="cai-att-items">
                                            {atts.map((a, i) => (
                                                a.type === 'application/pdf' ? (
                                                    <a key={i} href={a.url} target="_blank" rel="noopener noreferrer" className="cai-att-file" title={a.name}>
                                                        📄 {a.name}
                                                    </a>
                                                ) : (
                                                    <a key={i} href={a.url} target="_blank" rel="noopener noreferrer" className="cai-att-thumb" title={a.name}>
                                                        <img src={a.url} alt={a.name} loading="lazy" />
                                                    </a>
                                                )
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {m.status === 'replied' && m.reply && (
                                    <div className="cai-reply">
                                        <span className="cai-reply-label">คำตอบ ({m.replied_by_name} · {fmt(m.replied_at)}):</span>
                                        <div className="cai-reply-text">{m.reply}</div>
                                    </div>
                                )}

                                {/* ปุ่มตอบ / ฟอร์มตอบ */}
                                {replyFor === m.id ? (
                                    <div className="cai-reply-form">
                                        <textarea
                                            value={replyText}
                                            onChange={e => setReplyText(e.target.value.slice(0, 2000))}
                                            placeholder="พิมพ์คำตอบ... (จะส่งทางอีเมลถึงผู้ใช้)"
                                            rows="3"
                                            maxLength="2000"
                                        />
                                        <div className="cai-reply-actions">
                                            <span className="cai-count">{replyText.length}/2000 · ส่งไปที่ {m.email}</span>
                                            <div style={{ display: 'flex', gap: 8 }}>
                                                <button className="steam-btn steam-btn-secondary" onClick={() => setReplyFor(null)} disabled={sending}>ยกเลิก</button>
                                                <button className="steam-btn steam-btn-primary" onClick={() => sendReply(m.id)} disabled={sending}>
                                                    {sending ? 'กำลังส่ง...' : 'ส่งคำตอบทางอีเมล'}
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                ) : (
                                    <button
                                        className="steam-btn steam-btn-primary cai-reply-btn"
                                        onClick={() => { setReplyFor(m.id); setReplyText(m.reply || ''); setMessage({ type: '', text: '' }); }}
                                    >
                                        {m.status === 'replied' ? 'ตอบเพิ่มเติม' : 'ตอบกลับทางอีเมล'}
                                    </button>
                                )}
                            </div>
                        </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

export default AdminContact;
